import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatINR, formatApiErrorDetail } from "@/api/client";
import { toast } from "sonner";
import {
  Search, Filter, RefreshCw, X, Download, Eye, Truck, Package,
  IndianRupee, TrendingUp, BadgeCheck,
} from "lucide-react";

const STATUS_OPTIONS = [
  "placed", "confirmed", "packed", "shipped", "out_for_delivery",
  "delivered", "cancelled", "return_requested", "returned", "refunded",
];
const PAY_STATUSES = ["pending", "paid", "refunded", "failed"];

const STATUS_COLOR = {
  placed: "bg-blue-100 text-blue-700",
  confirmed: "bg-blue-100 text-blue-700",
  packed: "bg-indigo-100 text-indigo-700",
  shipped: "bg-purple-100 text-purple-700",
  out_for_delivery: "bg-amber-100 text-amber-700",
  delivered: "bg-green-100 text-green-700",
  cancelled: "bg-red-100 text-red-700",
  return_requested: "bg-amber-100 text-amber-700",
  returned: "bg-amber-100 text-amber-700",
  refunded: "bg-green-100 text-green-700",
};

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [payStatus, setPayStatus] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [report, setReport] = useState(null);
  const [view, setView] = useState(null); // order detail modal

  const fetch = async (override = {}) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      const _q = override.q !== undefined ? override.q : q;
      const _s = override.status !== undefined ? override.status : status;
      const _p = override.payStatus !== undefined ? override.payStatus : payStatus;
      const _from = override.fromDate !== undefined ? override.fromDate : fromDate;
      const _to = override.toDate !== undefined ? override.toDate : toDate;
      if (_q) params.set("q", _q);
      if (_s !== "all") params.set("status", _s);
      if (_p !== "all") params.set("payment_status", _p);
      if (_from) params.set("from_date", _from);
      if (_to) params.set("to_date", _to + "T23:59:59");
      const { data } = await api.get(`/admin/orders?${params.toString()}`);
      setOrders(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load orders");
    } finally { setLoading(false); }
  };

  const fetchReport = async () => {
    try {
      const { data } = await api.get("/admin/reports/sales?days=30");
      setReport(data);
    } catch { /* silent */ }
  };

  useEffect(() => { fetch(); fetchReport(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { fetch(); /* eslint-disable-next-line */ }, [status, payStatus]);

  const exportCsv = async () => {
    try {
      const token = localStorage.getItem("fah_token");
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (status !== "all") params.set("status", status);
      if (payStatus !== "all") params.set("payment_status", payStatus);
      if (fromDate) params.set("from_date", fromDate);
      if (toDate) params.set("to_date", toDate + "T23:59:59");
      const resp = await window.fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/orders.csv?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error("Export failed");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `fah_orders_${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch (e) {
      toast.error("Export failed");
    }
  };

  const updateStatus = async (order, newStatus) => {
    try {
      const payload = { status: newStatus };
      // Ask for tracking when going to shipped (only if not set yet)
      if (newStatus === "shipped" && !order.tracking_id) {
        const courier = window.prompt("Courier name (e.g., Delhivery, Bluedart) — optional:");
        const trackingId = window.prompt("Tracking ID (leave blank to auto-generate):");
        if (courier) payload.courier_name = courier;
        if (trackingId) payload.tracking_id = trackingId;
      }
      await api.post(`/orders/${order.id}/status`, payload);
      toast.success(`Order ${order.order_number} → ${newStatus.replace(/_/g, " ")}`);
      fetch(); fetchReport();
      if (view?.id === order.id) {
        const { data } = await api.get(`/orders/${order.id}`);
        setView(data);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    }
  };

  const counts = useMemo(() => {
    const c = { total: orders.length, paid: 0, pending_payment: 0, delivered: 0, cancelled: 0, revenue: 0 };
    for (const o of orders) {
      if (o.payment_status === "paid") c.paid++;
      if (o.payment_status === "pending") c.pending_payment++;
      if (o.status === "delivered") c.delivered++;
      if (o.status === "cancelled") c.cancelled++;
      if (!["cancelled", "refunded"].includes(o.status)) c.revenue += Number(o.total || 0);
    }
    return c;
  }, [orders]);

  return (
    <div data-testid="admin-orders-module">
      {/* Sales report cards */}
      {report && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          <Kpi icon={IndianRupee} color="bg-green-100 text-green-700" label="Revenue (30d)" value={formatINR(report.totals.revenue)} />
          <Kpi icon={Package} color="bg-blue-100 text-blue-700" label="Orders (30d)" value={report.totals.orders} />
          <Kpi icon={BadgeCheck} color="bg-emerald-100 text-emerald-700" label="Delivered" value={report.totals.delivered} />
          <Kpi icon={TrendingUp} color="bg-rose-100 text-rose-700" label="Cancelled" value={report.totals.cancelled} />
        </div>
      )}

      {/* Filters */}
      <div className="card-fah p-3 sm:p-4 mb-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 bg-muted/40 border border-border rounded-lg px-3 py-2 flex-1 min-w-[220px]">
            <Search className="w-4 h-4 text-muted-foreground" />
            <input
              value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && fetch()}
              placeholder="Search by Order ID, customer name, mobile, seller, tracking…"
              className="flex-1 text-sm focus:outline-none bg-transparent"
              data-testid="admin-orders-search"
            />
            {q && <button onClick={() => { setQ(""); fetch({ q: "" }); }}><X className="w-4 h-4 text-muted-foreground" /></button>}
          </div>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="text-sm px-3 py-2 border border-border rounded-lg bg-white" data-testid="admin-orders-status-filter">
            <option value="all">All Status</option>
            {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
          </select>
          <select value={payStatus} onChange={(e) => setPayStatus(e.target.value)} className="text-sm px-3 py-2 border border-border rounded-lg bg-white" data-testid="admin-orders-pay-filter">
            <option value="all">All Payment</option>
            {PAY_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="text-sm px-3 py-2 border border-border rounded-lg bg-white" data-testid="admin-orders-from" />
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="text-sm px-3 py-2 border border-border rounded-lg bg-white" data-testid="admin-orders-to" />
          <button onClick={() => fetch()} className="bg-muted text-foreground text-sm font-semibold px-3 py-2 rounded-lg inline-flex items-center gap-1.5" data-testid="admin-orders-apply"><Filter className="w-3.5 h-3.5" />Apply</button>
          <button onClick={() => { setQ(""); setStatus("all"); setPayStatus("all"); setFromDate(""); setToDate(""); fetch({ q: "", status: "all", payStatus: "all", fromDate: "", toDate: "" }); }} className="text-xs text-muted-foreground hover:text-primary inline-flex items-center gap-1" data-testid="admin-orders-clear"><RefreshCw className="w-3 h-3" />Reset</button>
          <button onClick={exportCsv} className="bg-secondary text-secondary-foreground text-sm font-bold px-4 py-2 rounded-lg flex items-center gap-1.5 ml-auto" data-testid="admin-orders-export"><Download className="w-4 h-4" />Export CSV</button>
        </div>
        <div className="mt-2 text-xs text-muted-foreground">
          Showing <b>{counts.total}</b> orders · Revenue <b>{formatINR(counts.revenue)}</b> · {counts.paid} paid · {counts.pending_payment} pending payment · {counts.delivered} delivered · {counts.cancelled} cancelled
        </div>
      </div>

      {/* Orders table */}
      <div className="card-fah overflow-x-auto" data-testid="admin-orders-table">
        <table className="w-full text-sm min-w-[1100px]">
          <thead className="bg-muted/40 text-left">
            <tr>{["Order #", "Date", "Customer", "Mobile", "Items", "Total", "Payment", "Status", "Tracking", "Actions"].map((h) => (
              <th key={h} className="px-3 py-3 text-[11px] uppercase tracking-wide text-muted-foreground whitespace-nowrap">{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="10" className="px-3 py-10 text-center text-muted-foreground">Loading orders…</td></tr>}
            {!loading && orders.length === 0 && <tr><td colSpan="10" className="px-3 py-10 text-center text-muted-foreground">No orders match these filters.</td></tr>}
            {orders.map((o) => {
              const addr = o.address || {};
              return (
                <tr key={o.id} className="border-t border-border hover:bg-muted/20" data-testid={`admin-order-row-${o.order_number}`}>
                  <td className="px-3 py-2.5 font-semibold text-primary">{o.order_number}</td>
                  <td className="px-3 py-2.5 text-xs whitespace-nowrap">{new Date(o.created_at).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" })}</td>
                  <td className="px-3 py-2.5"><div className="font-medium">{addr.full_name || o.buyer_name}</div><div className="text-[11px] text-muted-foreground line-clamp-1 max-w-[180px]">{addr.line1}{addr.city ? `, ${addr.city}` : ""}</div></td>
                  <td className="px-3 py-2.5 whitespace-nowrap">{addr.phone || "-"}</td>
                  <td className="px-3 py-2.5">
                    <div className="flex -space-x-2">
                      {o.items.slice(0, 3).map((it, i) => <img key={i} src={it.image} alt="" className="w-7 h-7 rounded border-2 border-white object-cover" title={it.name} />)}
                      {o.items.length > 3 && <span className="w-7 h-7 rounded bg-muted text-[10px] flex items-center justify-center border-2 border-white">+{o.items.length - 3}</span>}
                    </div>
                  </td>
                  <td className="px-3 py-2.5 font-semibold whitespace-nowrap">{formatINR(o.total)}</td>
                  <td className="px-3 py-2.5 text-xs">
                    <div className="font-semibold uppercase">{o.payment_method}</div>
                    <div className={`text-[10px] font-bold uppercase ${o.payment_status === "paid" ? "text-green-700" : o.payment_status === "refunded" ? "text-amber-700" : "text-muted-foreground"}`}>{o.payment_status}</div>
                  </td>
                  <td className="px-3 py-2.5">
                    <select value={o.status} onChange={(e) => updateStatus(o, e.target.value)} className={`text-[11px] font-bold uppercase px-2 py-1 rounded border-0 focus:outline-none ${STATUS_COLOR[o.status]}`} data-testid={`admin-order-status-${o.order_number}`}>
                      {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2.5 text-xs">
                    {o.tracking_id ? <div><span className="font-mono">{o.tracking_id}</span><div className="text-[10px] text-muted-foreground">{o.courier_name || ""}</div></div> : <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-1">
                      <button onClick={() => setView(o)} className="p-1.5 rounded hover:bg-muted text-foreground" title="View" data-testid={`admin-order-view-${o.order_number}`}><Eye className="w-4 h-4" /></button>
                      <Link to={`/order/${o.id}`} className="p-1.5 rounded hover:bg-muted text-primary" title="Open" target="_blank"><Truck className="w-4 h-4" /></Link>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Top sellers / products */}
      {report && (report.top_sellers.length > 0 || report.top_products.length > 0) && (
        <div className="grid sm:grid-cols-2 gap-4 mt-4">
          <div className="card-fah p-4">
            <div className="text-xs font-bold uppercase tracking-wide text-primary mb-2">Top Sellers (30d)</div>
            {report.top_sellers.slice(0, 5).map((s) => (
              <div key={s.seller_id} className="flex justify-between items-center text-sm py-1.5 border-t border-border first:border-0">
                <span className="font-medium">{s.seller_name}</span>
                <span className="font-bold">{formatINR(s.revenue)}</span>
              </div>
            ))}
          </div>
          <div className="card-fah p-4">
            <div className="text-xs font-bold uppercase tracking-wide text-primary mb-2">Top Products (30d)</div>
            {report.top_products.slice(0, 5).map((p) => (
              <div key={p.product_id} className="flex justify-between items-center gap-2 text-sm py-1.5 border-t border-border first:border-0">
                <div className="flex items-center gap-2 min-w-0">
                  <img src={p.image} alt="" className="w-7 h-7 rounded object-cover" />
                  <span className="truncate">{p.name}</span>
                </div>
                <span className="font-bold whitespace-nowrap">{formatINR(p.revenue)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick view modal */}
      {view && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4 overflow-y-auto" onClick={() => setView(null)} data-testid="admin-order-modal">
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-border px-4 py-3 flex items-center justify-between z-10">
              <div>
                <h2 className="font-display font-bold">{view.order_number}</h2>
                <div className="text-xs text-muted-foreground">{new Date(view.created_at).toLocaleString("en-IN")}</div>
              </div>
              <button onClick={() => setView(null)} className="p-1.5 hover:bg-muted rounded"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-4 space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><div className="text-xs uppercase text-muted-foreground">Customer</div><div className="font-semibold">{view.address?.full_name || view.buyer_name}</div><div className="text-xs text-muted-foreground">{view.address?.phone}</div></div>
                <div><div className="text-xs uppercase text-muted-foreground">Total</div><div className="font-bold text-lg">{formatINR(view.total)}</div></div>
              </div>
              <div><div className="text-xs uppercase text-muted-foreground mb-1">Address</div><div>{view.address?.line1}{view.address?.line2 ? `, ${view.address.line2}` : ""}, {view.address?.city}, {view.address?.state} - {view.address?.pincode}{view.address?.landmark ? ` (${view.address.landmark})` : ""}</div></div>
              <div>
                <div className="text-xs uppercase text-muted-foreground mb-1">Items</div>
                <div className="space-y-1.5">
                  {view.items.map((it, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <img src={it.image} alt="" className="w-9 h-9 rounded object-cover" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium line-clamp-1">{it.name}</div>
                        <div className="text-[11px] text-muted-foreground">SKU {it.sku || "—"} · {it.seller_name} · ×{it.qty}</div>
                      </div>
                      <div className="font-semibold">{formatINR((it.price || 0) * (it.qty || 1))}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><div className="text-xs uppercase text-muted-foreground">Payment</div><div className="font-semibold uppercase">{view.payment_method}</div><div className="text-xs uppercase">{view.payment_status}</div></div>
                <div>
                  <div className="text-xs uppercase text-muted-foreground">Update Status</div>
                  <select value={view.status} onChange={(e) => updateStatus(view, e.target.value)} className="mt-1 w-full text-sm px-3 py-2 border border-border rounded-lg bg-white">
                    {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Kpi = ({ icon: Icon, color, label, value }) => (
  <div className="card-fah p-3 flex items-center gap-3">
    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}><Icon className="w-5 h-5" /></div>
    <div>
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="font-bold">{value}</div>
    </div>
  </div>
);

export default AdminOrders;
